import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import '../components/KpiInfoTooltip.css'

export default function ExportHoverTooltip({
  content,
  children,
  className = '',
  as: Component = 'span',
  ...props
}) {
  const tooltipId = useId()
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0, placement: 'bottom' })

  const updatePosition = useCallback(() => {
    const el = anchorRef.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    const margin = 8
    const approxHeight = 72
    const spaceAbove = rect.top - margin
    const spaceBelow = window.innerHeight - rect.bottom - margin
    const placement = spaceBelow >= approxHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top'
    const halfWidth = 110
    const x = Math.min(
      Math.max(rect.left + rect.width / 2, halfWidth + margin),
      window.innerWidth - halfWidth - margin
    )
    const y = placement === 'bottom' ? rect.bottom + 10 : rect.top - 10

    setPosition({ x, y, placement })
  }, [])

  const show = () => {
    if (!content) return
    updatePosition()
    setOpen(true)
  }

  const hide = () => setOpen(false)

  useEffect(() => {
    if (!open) return undefined

    const handleReposition = () => requestAnimationFrame(updatePosition)
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [open, updatePosition])

  return (
    <>
      <Component
        ref={anchorRef}
        className={className}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? tooltipId : undefined}
        {...props}
      >
        {children}
      </Component>
      {open &&
        content &&
        createPortal(
          <div
            id={tooltipId}
            className={`kpiInfoTooltipPortal kpiInfoTooltipPortal--${position.placement}`}
            role="tooltip"
            style={{
              position: 'fixed',
              left: position.x,
              top: position.y,
              transform:
                position.placement === 'bottom'
                  ? 'translate(-50%, 0)'
                  : 'translate(-50%, -100%)',
              zIndex: 6000,
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  )
}
